import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
} from '@mui/material';
import {
  Science,
  Build,
  VerifiedUser,
  Assignment,
  Person,
  Dashboard,
  AccessTime,
  TrendingUp,
} from '@mui/icons-material';

// Import all laboratory dashboards
import SAALaboratoryDashboard from './SAALaboratoryDashboard';
import ICPLaboratoryDashboard from './ICPLaboratoryDashboard';
import AULaboratoryDashboard from './AULaboratoryDashboard';
import VolumetrieLaboratoryDashboard from './VolumetrieLaboratoryDashboard';
import PreparationMecaniqueDashboard from './PreparationMecaniqueDashboard';
import ValidationServiceDashboard from './ValidationServiceDashboard';
import SAAPreparationChimiqueDashboard from '../dashboards/SAAPreparationChimiqueDashboard';
import ICPPreparationChimiqueDashboard from '../dashboards/ICPPreparationChimiqueDashboard';
import ICPSupervisorDashboard from '../dashboards/ICPSupervisorDashboard';
import ICPAnalystDashboard from '../dashboards/ICPAnalystDashboard';
import AUPreparationChimiqueDashboard from '../dashboards/AUPreparationChimiqueDashboard';
import VolumetriePreparationChimiqueDashboard from '../dashboards/VolumetriePreparationChimiqueDashboard';

// Import laboratory access service
import { laboratoryAccessService, UserLaboratoryAccess } from '../../services/laboratoryAccessService';

interface UserProfile {
  id: string;
  name: string;
  role: 'ANALYSTE' | 'TECHNICIEN' | 'SUPERVISEUR' | 'ADMIN' | 'SAA_PREP_CHIMIQUE' | 'ICP_PREP_CHIMIQUE' | 'AU_PREP_CHIMIQUE' | 'VOLUMETRIE_PREP_CHIMIQUE' | 'PREPARATION_MECANIQUE' | 'VALIDATION_SERVICE' | 'SAA_ANALYST' | 'ICP_ANALYST' | 'AU_ANALYST' | 'VOLUMETRIE_ANALYST';
  assigned_laboratories: string[];
  permissions: string[];
}

interface LaboratoryInfo {
  code: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  active_samples: number;
  pending_tasks: number;
}

interface LaboratoryRouterProps {
  userId: string;
}

const LaboratoryRouter: React.FC<LaboratoryRouterProps> = ({ userId }) => {
  const [searchParams] = useSearchParams();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userAccess, setUserAccess] = useState<UserLaboratoryAccess | null>(null);
  const [selectedLaboratory, setSelectedLaboratory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [laboratoryStats, setLaboratoryStats] = useState<{[key: string]: any}>({});

  // Map URL parameters to laboratory codes
  const getLabCodeFromParam = (labParam: string): string | null => {
    const labMap: {[key: string]: string} = {
      'saa': 'LABORATOIRE_SAA',
      'icp': 'LABORATOIRE_ICP',
      'au': 'LABORATOIRE_AU',
      'volumetrie': 'LABORATOIRE_VOLUMETRIE',
      'preparation': 'PREPARATION_MECANIQUE',
      'validation': 'VALIDATION_SERVICE'
    };
    return labMap[labParam] || null;
  };

  const laboratories: LaboratoryInfo[] = [
    {
      code: 'PREPARATION_MECANIQUE',
      name: 'Atelier de Préparation Mécanique',
      description: 'Concassage, broyage, tamisage des échantillons',
      icon: <Build />,
      color: '#ff9800',
      active_samples: laboratoryStats['PREPARATION_MECANIQUE']?.totalSamples || 0,
      pending_tasks: laboratoryStats['PREPARATION_MECANIQUE']?.pendingSamples || 0,
    },
    {
      code: 'LABORATOIRE_SAA',
      name: 'Laboratoire SAA',
      description: 'Analyse par Aqua Regia et Absorption Atomique',
      icon: <Science />,
      color: '#2196f3',
      active_samples: laboratoryStats['LABORATOIRE_SAA']?.totalSamples || 0,
      pending_tasks: laboratoryStats['LABORATOIRE_SAA']?.pendingSamples || 0,
    },
    {
      code: 'LABORATOIRE_ICP',
      name: 'Laboratoire ICP',
      description: 'Analyse par Plasma Couplé Inductif',
      icon: <Science />,
      color: '#9c27b0',
      active_samples: laboratoryStats['LABORATOIRE_ICP']?.totalSamples || 0,
      pending_tasks: laboratoryStats['LABORATOIRE_ICP']?.pendingSamples || 0,
    },
    {
      code: 'LABORATOIRE_AU',
      name: 'Laboratoire AU',
      description: 'Analyse de l\'or par coupellation et AAS',
      icon: <Science />,
      color: '#ff9800',
      active_samples: laboratoryStats['LABORATOIRE_AU']?.totalSamples || 0,
      pending_tasks: laboratoryStats['LABORATOIRE_AU']?.pendingSamples || 0,
    },
    {
      code: 'LABORATOIRE_VOLUMETRIE',
      name: 'Laboratoire Volumétrie',
      description: 'Analyses par titrations volumétriques',
      icon: <Science />,
      color: '#4caf50',
      active_samples: laboratoryStats['LABORATOIRE_VOLUMETRIE']?.totalSamples || 0,
      pending_tasks: laboratoryStats['LABORATOIRE_VOLUMETRIE']?.pendingSamples || 0,
    },
    {
      code: 'VALIDATION_SERVICE',
      name: 'Service de Validation',
      description: 'Validation des résultats et génération de certificats',
      icon: <VerifiedUser />,
      color: '#f44336',
      active_samples: laboratoryStats['VALIDATION_SERVICE']?.totalSamples || 0,
      pending_tasks: laboratoryStats['VALIDATION_SERVICE']?.pendingSamples || 0,
    },
  ];

  useEffect(() => {
    loadUserProfile();
  }, [userId]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 LaboratoryRouter: Loading user profile for userId:', userId);
      
      // Try to get user laboratory access from API
      let access: UserLaboratoryAccess | null = null;
      let profile: UserProfile | null = null;
      
      try {
        access = await laboratoryAccessService.getUserAccess();
        console.log('✅ LaboratoryRouter: Got user access from API:', access);
      } catch (apiError) {
        console.warn('⚠️ LaboratoryRouter: API not available, using fallback logic:', apiError);
        
        // Fallback: Create access based on user email and role from auth context
        // We need to get the user from auth context since we can't rely on API
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        console.log('📋 LaboratoryRouter: Current user from localStorage:', currentUser);
        
        access = await createFallbackAccess(currentUser);
        console.log('🔧 LaboratoryRouter: Created fallback access:', access);
      }
      
      if (access) {
        setUserAccess(access);
        
        // Get the current user from auth context to get the real role
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        console.log('👤 LaboratoryRouter: Using current user for profile:', currentUser);
        console.log('🔍 LaboratoryRouter: Current user email:', currentUser.email);
        console.log('🔍 LaboratoryRouter: Current user role:', currentUser.role);
        
        // Convert API response to local UserProfile format
        profile = {
          id: access.userId,
          name: currentUser.firstName && currentUser.lastName 
            ? `${currentUser.firstName} ${currentUser.lastName}` 
            : 'Utilisateur Laboratoire',
          role: currentUser.role || 'ANALYSTE', // Use actual role from auth context
          assigned_laboratories: access.accessibleLaboratories,
          permissions: Object.keys(access.permissions),
        };
        
        console.log('✅ LaboratoryRouter: Created user profile:', profile);
        setUserProfile(profile);
        
        // Skip loading statistics for specialized preparation users since they auto-route
        const isSpecializedUser = ['SAA_PREP_CHIMIQUE', 'ICP_PREP_CHIMIQUE', 'AU_PREP_CHIMIQUE', 'VOLUMETRIE_PREP_CHIMIQUE', 'PREPARATION_MECANIQUE', 'VALIDATION_SERVICE'].includes(currentUser.role);
        
        if (!isSpecializedUser) {
          // Load statistics for accessible laboratories
          await loadLaboratoryStatistics(access.accessibleLaboratories);
        } else {
          console.log('⚡ Skipping statistics loading for specialized user:', currentUser.role);
        }
      }
      
    } catch (error) {
      console.error('❌ LaboratoryRouter: Error loading user profile:', error);
      setError('Erreur lors du chargement du profil utilisateur. Vérifiez votre connexion.');
    } finally {
      setLoading(false);
    }
  };

  // Fallback function to create access based on user email/role
  const createFallbackAccess = async (user: any): Promise<UserLaboratoryAccess> => {
    console.log('🔧 Creating fallback access for user:', user);
    console.log('🔧 User email:', user.email);
    console.log('🔧 User role:', user.role);
    
    const accessibleLaboratories: string[] = [];
    const permissions: UserLaboratoryAccess['permissions'] = {};
    
    // Handle preparation chimique roles first (more specific)
    if (user.email?.includes('saa.prep@afrilab.ma') || user.role === 'SAA_PREP_CHIMIQUE') {
      accessibleLaboratories.push('LABORATOIRE_SAA');
      permissions['LABORATOIRE_SAA'] = {
        canPrepChemicals: true,
        canPerformAnalysis: false, // Prep users can't perform analysis
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: true,
      };
    } else if (user.email?.includes('icp.prep@afrilab.ma') || user.role === 'ICP_PREP_CHIMIQUE') {
      accessibleLaboratories.push('LABORATOIRE_ICP');
      permissions['LABORATOIRE_ICP'] = {
        canPrepChemicals: true,
        canPerformAnalysis: false,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: true,
      };
    } else if (user.email?.includes('au.prep@afrilab.ma') || user.role === 'AU_PREP_CHIMIQUE') {
      accessibleLaboratories.push('LABORATOIRE_AU');
      permissions['LABORATOIRE_AU'] = {
        canPrepChemicals: true,
        canPerformAnalysis: false,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: true,
      };
    } else if (user.email?.includes('vol.prep@afrilab.ma') || user.role === 'VOLUMETRIE_PREP_CHIMIQUE') {
      accessibleLaboratories.push('LABORATOIRE_VOLUMETRIE');
      permissions['LABORATOIRE_VOLUMETRIE'] = {
        canPrepChemicals: true,
        canPerformAnalysis: false,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: true,
      };
    }
    // Handle analyst roles (can perform analysis but not prep)
    else if (user.email?.includes('saa@afrilab.ma') || user.email?.includes('sup.saa@afrilab.ma') || user.role === 'SAA_ANALYST') {
      accessibleLaboratories.push('LABORATOIRE_SAA');
      permissions['LABORATOIRE_SAA'] = {
        canPrepChemicals: false,
        canPerformAnalysis: true,
        canValidateResults: user.email?.includes('sup.') || user.role?.includes('SUPERVISOR'),
        canAssignSamples: user.email?.includes('sup.') || user.role?.includes('SUPERVISOR'),
        canViewAllSamples: true,
      };
      
      // Set role as supervisor for sup.saa users
      if (user.email?.includes('sup.saa@afrilab.ma') && userProfile) {
        userProfile.role = 'LABORATORY_MANAGER' as any;
      }
    } else if (user.email?.includes('icp@afrilab.ma') || user.email?.includes('sup.icp@afrilab.ma') || user.role === 'ICP_ANALYST') {
      accessibleLaboratories.push('LABORATOIRE_ICP');
      permissions['LABORATOIRE_ICP'] = {
        canPrepChemicals: false,
        canPerformAnalysis: true,
        canValidateResults: user.email?.includes('sup.') || user.role?.includes('SUPERVISOR'),
        canAssignSamples: user.email?.includes('sup.') || user.role?.includes('SUPERVISOR'),
        canViewAllSamples: true,
      };
      
      // Set role as supervisor for sup.icp users
      if (user.email?.includes('sup.icp@afrilab.ma') && userProfile) {
        userProfile.role = 'LABORATORY_MANAGER' as any;
      }
    } else if (user.email?.includes('au@afrilab.ma') || user.email?.includes('sup.au@afrilab.ma') || user.role === 'AU_ANALYST') {
      accessibleLaboratories.push('LABORATOIRE_AU');
      permissions['LABORATOIRE_AU'] = {
        canPrepChemicals: false,
        canPerformAnalysis: true,
        canValidateResults: user.email?.includes('sup.') || user.role?.includes('SUPERVISOR'),
        canAssignSamples: user.email?.includes('sup.') || user.role?.includes('SUPERVISOR'),
        canViewAllSamples: true,
      };
    } else if (user.email?.includes('volumetrie@afrilab.ma') || user.email?.includes('sup.vol@afrilab.ma') || user.role === 'VOLUMETRIE_ANALYST') {
      accessibleLaboratories.push('LABORATOIRE_VOLUMETRIE');
      permissions['LABORATOIRE_VOLUMETRIE'] = {
        canPrepChemicals: false,
        canPerformAnalysis: true,
        canValidateResults: user.email?.includes('sup.') || user.role?.includes('SUPERVISOR'),
        canAssignSamples: user.email?.includes('sup.') || user.role?.includes('SUPERVISOR'),
        canViewAllSamples: true,
      };
    }
    
    // Handle mechanical preparation
    if (user.email?.includes('preparation@afrilab.ma') || user.role === 'PREPARATION_MECANIQUE') {
      accessibleLaboratories.push('PREPARATION_MECANIQUE');
      permissions['PREPARATION_MECANIQUE'] = {
        canPrepChemicals: true,
        canPerformAnalysis: false,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: false,
      };
    }
    
    // Handle validation service
    if (user.role === 'VALIDATION_SERVICE') {
      accessibleLaboratories.push('VALIDATION_SERVICE');
      permissions['VALIDATION_SERVICE'] = {
        canPrepChemicals: false,
        canPerformAnalysis: false,
        canValidateResults: true,
        canAssignSamples: true,
        canViewAllSamples: true,
      };
    }
    
    // Handle general roles - BUT ONLY if no specific laboratory assignment was made above
    if ((user.role === 'LABORATORY_MANAGER' || user.role === 'ADMIN') && accessibleLaboratories.length === 0) {
      // Only give full access to true admins, not laboratory-specific supervisors
      if (user.role === 'ADMIN' || (user.role === 'LABORATORY_MANAGER' && !user.email?.includes('@afrilab.ma'))) {
        accessibleLaboratories.push(
          'LABORATOIRE_SAA', 
          'LABORATOIRE_ICP', 
          'LABORATOIRE_AU', 
          'LABORATOIRE_VOLUMETRIE',
          'PREPARATION_MECANIQUE',
          'VALIDATION_SERVICE'
        );
        
        // Full permissions for managers/admins
        const fullPermissions = {
          canPrepChemicals: true,
          canPerformAnalysis: true,
          canValidateResults: true,
          canAssignSamples: true,
          canViewAllSamples: true,
        };
        
        accessibleLaboratories.forEach(lab => {
          permissions[lab] = fullPermissions;
        });
      }
    }
    
    // If no specific lab found but user is analyst, give access to SAA as default
    if (accessibleLaboratories.length === 0 && user.role === 'ANALYST') {
      accessibleLaboratories.push('LABORATOIRE_SAA');
      permissions['LABORATOIRE_SAA'] = {
        canPrepChemicals: true,
        canPerformAnalysis: true,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: true,
      };
    }
    
    console.log('🔧 Fallback access created:', { accessibleLaboratories, permissions });
    
    return {
      userId: user.id || userId,
      accessibleLaboratories,
      permissions,
    };
  };

  const loadLaboratoryStatistics = async (accessibleLabs: string[]) => {
    const stats: {[key: string]: any} = {};
    
    // Load statistics for each accessible laboratory
    const promises = accessibleLabs.map(async (lab) => {
      try {
        const labStats = await laboratoryAccessService.getLaboratoryStatistics(lab);
        stats[lab] = labStats;
      } catch (error) {
        console.error(`Error loading stats for ${lab}:`, error);
        // Use mock data as fallback
        stats[lab] = {
          totalSamples: Math.floor(Math.random() * 20) + 5,
          pendingSamples: Math.floor(Math.random() * 10) + 1,
        };
      }
    });
    
    await Promise.all(promises);
    setLaboratoryStats(stats);
  };

  const handleLaboratorySelect = (laboratoryCode: string) => {
    setSelectedLaboratory(laboratoryCode);
  };

  const handleBackToRouter = () => {
    setSelectedLaboratory(null);
  };

  const canAccessLaboratory = (laboratoryCode: string): boolean => {
    if (!userAccess) return false;
    
    // Check if user has access to this laboratory
    return userAccess.accessibleLaboratories.includes(laboratoryCode) ||
           laboratoryCode === 'PREPARATION_MECANIQUE'; // Everyone can access mechanical prep
  };

  const getUserPermissionsForLab = (laboratoryCode: string) => {
    if (!userAccess || !userAccess.permissions[laboratoryCode]) {
      return {
        canPrepChemicals: false,
        canPerformAnalysis: false,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: false,
      };
    }
    
    return userAccess.permissions[laboratoryCode];
  };

  // Check if user should be auto-routed to a specific dashboard based on their role
  const getAutoRouteDashboard = (): string | null => {
    if (!userProfile || !userAccess) return null;
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    // Auto-route specialized users to their specific dashboards
    const roleStr = userProfile.role as string;
    console.log('🎯 getAutoRouteDashboard: userProfile.role:', roleStr);
    console.log('🎯 getAutoRouteDashboard: localStorage user.role:', user.role);
    console.log('🎯 getAutoRouteDashboard: localStorage user.email:', user.email);
    
    switch (roleStr) {
      // Preparation chimique users
      case 'SAA_PREP_CHIMIQUE':
        return 'SAA_PREP_CHIMIQUE';
      case 'ICP_PREP_CHIMIQUE':
        return 'ICP_PREP_CHIMIQUE';
      case 'AU_PREP_CHIMIQUE':
        return 'AU_PREP_CHIMIQUE';
      case 'VOLUMETRIE_PREP_CHIMIQUE':
        return 'VOLUMETRIE_PREP_CHIMIQUE';
      case 'PREPARATION_MECANIQUE':
        return 'PREPARATION_MECANIQUE';
      case 'VALIDATION_SERVICE':
        return 'VALIDATION_SERVICE';
      
      // Analyst users - auto-route to their laboratory
      case 'SAA_ANALYST':
        return 'LABORATOIRE_SAA';
      case 'ICP_ANALYST':
        return 'LABORATOIRE_ICP';
      case 'AU_ANALYST':
        return 'LABORATOIRE_AU';
      case 'VOLUMETRIE_ANALYST':
        return 'LABORATOIRE_VOLUMETRIE';
      
      // Laboratory-specific supervisors should auto-route to their laboratory
      case 'LABORATORY_MANAGER': {
        // Prefer the explicit assigned_laboratories profile field when available
        const assignedLabs = userProfile?.assigned_laboratories || [];
        if (assignedLabs.length === 1) {
          return assignedLabs[0];
        }
        // Fallback: email-based routing (legacy production accounts before assigned_laboratories was populated)
        if (user.email?.includes('sup.saa@afrilab.ma')) {
          return 'LABORATOIRE_SAA';
        } else if (user.email?.includes('sup.icp@afrilab.ma')) {
          return 'LABORATOIRE_ICP';
        } else if (user.email?.includes('sup.au@afrilab.ma')) {
          return 'LABORATOIRE_AU';
        } else if (user.email?.includes('sup.vol@afrilab.ma')) {
          return 'LABORATOIRE_VOLUMETRIE';
        }
        // True laboratory managers (not specific supervisors) see selection interface
        return null;
      }
      
      // General supervisors see selection interface
      case 'QUALITY_MANAGER':
      case 'ADMIN':
        return null;
      
      default:
        return null;
    }
  };

  // Auto-route on load if user has specialized role OR URL parameter
  useEffect(() => {
    console.log('🔄 Auto-route useEffect triggered:', { userProfile, selectedLaboratory, userAccess });
    
    if (userProfile && userAccess && !selectedLaboratory) {
      // Check URL parameters first
      const labParam = searchParams.get('lab');
      if (labParam) {
        const labCode = getLabCodeFromParam(labParam); 
        if (labCode && canAccessLaboratory(labCode)) {
          console.log('🎯 URL parameter routing to:', labCode);
          setSelectedLaboratory(labCode);
          return;
        }
      }
      
      // Fallback to auto-routing for specialized users
      const autoRoute = getAutoRouteDashboard();
      console.log('🎯 Auto-route decision:', { role: userProfile.role, autoRoute, selectedLaboratory });
      
      if (autoRoute) {
        console.log('✅ Auto-routing to:', autoRoute);
        setSelectedLaboratory(autoRoute);
      }
    }
  }, [userProfile, userAccess, selectedLaboratory, searchParams]);

  // Also check for auto-routing when user profile is loaded
  useEffect(() => {
    if (userProfile && userAccess && !loading && !selectedLaboratory) {
      // Check URL parameters first
      const labParam = searchParams.get('lab');
      if (labParam) {
        const labCode = getLabCodeFromParam(labParam);
        if (labCode && canAccessLaboratory(labCode)) {
          console.log('🎯 Post-load URL parameter routing to:', labCode);
          setSelectedLaboratory(labCode);
          return;
        }
      }
      
      // Fallback to auto-routing for specialized users
      const autoRoute = getAutoRouteDashboard();
      console.log('🔄 Post-load auto-route check:', { role: userProfile.role, autoRoute });
      
      if (autoRoute) {
        console.log('✅ Post-load auto-routing to:', autoRoute);
        setSelectedLaboratory(autoRoute);
      }
    }
  }, [userProfile, userAccess, loading, searchParams]);

  const renderLaboratoryDashboard = () => {
    if (!userProfile || !userAccess) return null;
    
    // Add null check for selectedLaboratory
    if (!selectedLaboratory) return null;

    const labPermissions = getUserPermissionsForLab(selectedLaboratory);
    
    const mapUserRoleToDashboardRole = (role: string): 'ANALYSTE' | 'TECHNICIEN' | 'SUPERVISEUR' => {
      switch (role) {
        case 'SAA_ANALYST':
        case 'ICP_ANALYST':
        case 'AU_ANALYST':
        case 'VOLUMETRIE_ANALYST':
        case 'ANALYST':
          return 'ANALYSTE';
        case 'SAA_PREP_CHIMIQUE':
        case 'ICP_PREP_CHIMIQUE':
        case 'AU_PREP_CHIMIQUE':
        case 'VOLUMETRIE_PREP_CHIMIQUE':
        case 'PREPARATION_MECANIQUE':
        case 'TECHNICIAN_PREP':
          return 'TECHNICIEN';
        case 'LABORATORY_MANAGER':
        case 'QUALITY_MANAGER':
        case 'ADMIN':
          return 'SUPERVISEUR';
        default:
          return 'ANALYSTE'; // Default fallback
      }
    };
    
    const commonProps = {
      userRole: mapUserRoleToDashboardRole(userProfile.role),
      userId: userProfile.id,
      userAccess: userAccess,
      permissions: labPermissions,
      onBack: handleBackToRouter,
    };

    // Add debug logging for PREPARATION_MECANIQUE
    console.log('🔧 LaboratoryRouter renderLaboratoryDashboard:', {
      selectedLaboratory,
      userRole: userProfile.role,
      mappedRole: mapUserRoleToDashboardRole(userProfile.role),
      timestamp: new Date().toISOString()
    });

    // At this point selectedLaboratory is guaranteed to be non-null due to check above
    const labCode = selectedLaboratory as string;

    switch (labCode) {
      case 'PREPARATION_MECANIQUE':
        console.log('✅ RENDERING PreparationMecaniqueDashboard for PREPARATION_MECANIQUE');
        return <PreparationMecaniqueDashboard {...commonProps} />;
        
      // Specialized preparation chimique dashboards
      case 'SAA_PREP_CHIMIQUE':
        return <SAAPreparationChimiqueDashboard />;
      case 'ICP_PREP_CHIMIQUE':
        return <ICPPreparationChimiqueDashboard />;
      case 'AU_PREP_CHIMIQUE':
        return <AUPreparationChimiqueDashboard />;
      case 'VOLUMETRIE_PREP_CHIMIQUE':
        return <VolumetriePreparationChimiqueDashboard />;
        
      // General laboratory dashboards
      case 'LABORATOIRE_SAA':
        return <SAALaboratoryDashboard {...commonProps} />;
      case 'LABORATOIRE_ICP':
        // Check if user is ICP supervisor
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.email?.includes('sup.icp@afrilab.ma') || 
            commonProps.userRole === 'SUPERVISEUR') {
          return <ICPSupervisorDashboard />;
        }
        // Route ICP analysts to the specialized analyst dashboard with batch functionality
        return <ICPAnalystDashboard />;
      case 'LABORATOIRE_AU':
        return <AULaboratoryDashboard {...commonProps} />;
      case 'LABORATOIRE_VOLUMETRIE':
        return <VolumetrieLaboratoryDashboard {...commonProps} />;
      case 'VALIDATION_SERVICE':
        return <ValidationServiceDashboard 
          userRole="SUPERVISEUR" 
          userId={userProfile.id}
        />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Chargement...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // If a laboratory is selected, render its dashboard
  if (selectedLaboratory) {
    return (
      <Box>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Button onClick={handleBackToRouter} variant="outlined">
            ← Retour au tableau de bord
          </Button>
        </Box>
        {renderLaboratoryDashboard()}
      </Box>
    );
  }

  // Main laboratory router view
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Dashboard color="primary" />
          Tableau de Bord Laboratoires
        </Typography>
        
        {userProfile && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip
              icon={<Person />}
              label={`${userProfile.name} - ${userProfile.role}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`${userProfile.assigned_laboratories.length} laboratoire(s) assigné(s)`}
              color="secondary"
              variant="outlined"
            />
          </Box>
        )}
      </Box>

      {/* Laboratory Cards */}
      <Grid container spacing={3}>
        {laboratories
          .filter(lab => canAccessLaboratory(lab.code))
          .map((laboratory) => (
            <Grid item xs={12} sm={6} md={4} key={laboratory.code}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                  borderLeft: `4px solid ${laboratory.color}`,
                }}
                onClick={() => handleLaboratorySelect(laboratory.code)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: laboratory.color, mr: 2 }}>
                      {laboratory.icon}
                    </Box>
                    <Typography variant="h6" component="h2">
                      {laboratory.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {laboratory.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Échantillons actifs
                      </Typography>
                      <Typography variant="h6" color={laboratory.color}>
                        {laboratory.active_samples}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Tâches en attente
                      </Typography>
                      <Typography variant="h6" color="warning.main">
                        {laboratory.pending_tasks}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ 
                      mt: 2, 
                      backgroundColor: laboratory.color,
                      '&:hover': {
                        backgroundColor: laboratory.color,
                        opacity: 0.8,
                      }
                    }}
                  >
                    Accéder au Laboratoire
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>

      {/* Quick Stats */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Aperçu Rapide
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Assignment color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {laboratories.reduce((sum, lab) => sum + lab.active_samples, 0)}
                    </Typography>
                    <Typography color="text.secondary">
                      Total Échantillons Actifs
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTime color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {laboratories.reduce((sum, lab) => sum + lab.pending_tasks, 0)}
                    </Typography>
                    <Typography color="text.secondary">
                      Tâches en Attente
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUp color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {laboratories.filter(lab => canAccessLaboratory(lab.code)).length}
                    </Typography>
                    <Typography color="text.secondary">
                      Laboratoires Accessibles
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default LaboratoryRouter; 